import { planItemCreated, planItemPath, planSessionPath, TRAINING_ROUTES, } from "@repo/contract/training";
import { toApiError } from "../api/api-error";
import { readJson } from "../api/auth-api";
/**
 * The plan write a mid-session swap makes (`D06`), and nothing else.
 *
 * ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
 * Every function here was written elsewhere in the Expo app and carried here
 * verbatim when it was retired: the executor and the swap's reason key from
 * `deck/engine-proposer.ts`, the row diff and the exclusion call from
 * `training/plan-edit.ts`, and the three plan routes from
 * `training/training-api.ts`. Those modules died with the screens they
 * served (they survive in the pre-Capacitor backup tag); `substitution.ts`
 * did not, and these are exactly the pieces its `plan` and `never` scopes
 * call. The Week screen's gestures (`withItemAt`, `withItemMoved`,
 * `excludeAndApply`, …) stayed behind, because no session rule calls them.
 *
 * ── A PLAN WRITE DIFFS `PlanItemView` — THE ROW — AND NEVER A CARD (`T-156`)
 * A card's targets are NON-NULLABLE, because a card must print numbers, so a
 * write built from a card materialises `3 × 8–12` into a row the plan
 * deliberately left unspecified — on a hold, whose reps are `null` by
 * construction — after which `advance-plan` progresses against numbers no
 * protocol ever produced. Two 200s and a corrupt plan, with nothing red
 * anywhere. The mid-session swap shipped exactly that once. {@link planEditFor}
 * works on `PlanItemView`, where a null target stays null, and
 * {@link ReplacementChoice} carries an exercise's IDENTITY only, so a target
 * cannot physically enter the write.
 *
 * ── THE WRITE ORDER IS LOAD-BEARING ─────────────────────────────────────────
 * Deletes, then creates, then one PUT, sequentially — because the server
 * assigns positions in ARRIVAL order and `Promise.all` would order the
 * person's session by network timing. {@link applyPlanEdits} is the only
 * implementation of that rule.
 */
/**
 * The engine's key for a different-equipment swap, restated.
 *
 * `SUBSTITUTE_REASON_KEYS.otherEquipment` lives in `@repo/training`, which a
 * client must never import (`D118`: the app reads `@repo/contract`, never
 * `packages/modules/*`).
 */
export const OTHER_EQUIPMENT_REASON = "prescription.reason.same_muscle_other_equipment";
// ── The three plan routes the write needs ───────────────────────────────────
const JSON_HEADERS = { "content-type": "application/json" };
/** A JSON request with a replayable string body. */
function jsonBody(method, value) {
    return { body: JSON.stringify(value), headers: JSON_HEADERS, method };
}
const DELETE_REQUEST = { method: "DELETE" };
/**
 * A 204, or a throw. `readJson` cannot be used for a route with no body — it
 * would call `.json()` on an empty response and turn every success into
 * `error.internal` — but the failure path must stay identical, so
 * `toApiError` is still what a non-2xx becomes.
 */
async function readNoContent(response) {
    if (!response.ok) {
        throw await toApiError(response);
    }
}
/** `PUT /me/plan/sessions/:id` — the session's item order. */
function updatePlanSession(fetcher, sessionId, input) {
    return fetcher(planSessionPath(sessionId), jsonBody("PUT", input)).then(readNoContent);
}
/** `POST /me/plan/items` — an exercise into a session. */
function createPlanItem(fetcher, input) {
    return fetcher(TRAINING_ROUTES.planItems, jsonBody("POST", input)).then((response) => readJson(response, planItemCreated));
}
/**
 * `DELETE /me/plan/items/:id`. An item is immutable except for its position,
 * so a changed exercise is this call followed by `createPlanItem`.
 */
function deletePlanItem(fetcher, itemId) {
    return fetcher(planItemPath(itemId), DELETE_REQUEST).then(readNoContent);
}
/**
 * Run the diff, in the only order that works: deletes first (a delete after
 * the PUT would drop a row the order names), then creates (their answers are
 * the ids the order needs), then the one PUT. Sequential on purpose — the
 * server assigns positions in arrival order, and `Promise.all` would write
 * the session in network order.
 */
export async function applyPlanEdits(fetcher, edits) {
    for (const itemId of edits.deletes) {
        await deletePlanItem(fetcher, itemId);
    }
    const createdIds = [];
    for (const input of edits.creates) {
        const { itemId } = await createPlanItem(fetcher, input);
        createdIds.push(itemId);
    }
    if (edits.order !== null) {
        const remaining = [...createdIds];
        const itemIds = edits.order.map((ref) => {
            if (ref.kind === "existing") {
                return ref.itemId;
            }
            const created = remaining.shift();
            if (created === undefined) {
                throw new Error("plan edits name more created items than were created");
            }
            return created;
        });
        await updatePlanSession(fetcher, edits.sessionId, { itemIds });
    }
}
/**
 * The prefix marking an item the server has never seen.
 *
 * A real item id is a server-issued opaque string; this one is deliberately
 * unmistakable, so a pending id that ever escaped into a URL would 404 loudly
 * rather than address somebody else's row.
 */
const PENDING_PREFIX = "pending:";
/** The id an item that still has to be created is carried under. */
function pendingItemId(exerciseId) {
    return `${PENDING_PREFIX}${exerciseId}`;
}
/** Does this row still have to be POSTed? */
function isPending(item) {
    return item.itemId.startsWith(PENDING_PREFIX);
}
/**
 * The row that replaces another: the choice's exercise, the original's
 * prescription, under a pending id.
 *
 * Every reason key a substitution carries is `same_muscle_*`, so the muscle is
 * the original's by construction. The EQUIPMENT is kept from the original even
 * where the swap changed it, because `planItemView.exercise.equipment` needs a
 * catalogue id and `planItemAlternative` carries only a localised name. This
 * object lives for as long as one POST takes — the caller reconciles from the
 * server the moment the write lands — so a stale equipment word for that
 * instant is the cheapest of the available wrongs, and inventing an id would
 * be the most expensive.
 */
export function replacementItem(original, choice) {
    return {
        ...original,
        exercise: {
            ...original.exercise,
            clip: choice.clip,
            name: choice.name,
        },
        exerciseId: choice.exerciseId,
        itemId: pendingItemId(choice.exerciseId),
        // The engine's reason belonged to the exercise it was written about.
        reason: null,
    };
}
/**
 * A plan row as the body that re-creates it.
 *
 * Every target is carried across exactly as it stands, `null` included: `null`
 * is *"the plan prescribes this exercise and leaves the numbers to you"* and
 * defaulting one here would be the client inventing a prescription.
 * `targetDurationSeconds` (`D151`) is the one whose absence was invisible: a
 * timed pick has `null` reps BY CONSTRUCTION, so dropping its seconds leaves a
 * body that validates, a 201, and a plank with no hold. Spelt out rather than
 * spread, so a field added to `planItemView` is a decision here and not a
 * silent copy — `planItemInput` is a `z.strictObject`.
 */
function planItemInputFor(item, planId, sessionId) {
    return {
        exerciseId: item.exerciseId,
        notes: item.notes,
        planId,
        restSeconds: item.restSeconds,
        sessionId,
        targetDurationSeconds: item.targetDurationSeconds,
        targetLoad: item.targetLoad,
        targetRepsMax: item.targetRepsMax,
        targetRepsMin: item.targetRepsMin,
        targetSets: item.targetSets,
    };
}
/**
 * The calls that make the stored session match `next`.
 *
 * Rows are matched by `itemId`, never by exercise: an alternative that
 * happens to be an exercise already sitting elsewhere in the session would
 * otherwise mis-pair and the person would watch the wrong row disappear.
 *
 * The `PUT` is omitted when the deletes and the appends already leave the
 * session in the wanted order. A `PUT` that names the order the session
 * already has is a write that can fail, on a gesture that had no need of one.
 */
export function planEditFor(planId, sessionId, current, next) {
    const currentIds = new Set(current.map((item) => item.itemId));
    const keptIds = new Set(next
        .filter((item) => !isPending(item) && currentIds.has(item.itemId))
        .map((item) => item.itemId));
    const deletes = current
        .filter((item) => !keptIds.has(item.itemId))
        .map((item) => item.itemId);
    const created = next.filter((item) => !keptIds.has(item.itemId));
    const creates = created.map((item) => planItemInputFor(item, planId, sessionId));
    const desired = next.map((item) => keptIds.has(item.itemId)
        ? { kind: "existing", itemId: item.itemId }
        : { kind: "created", exerciseId: item.exerciseId });
    // What the session holds with no `PUT` at all: the kept rows in their
    // stored order, then the creates appended in the order they were POSTed.
    const predicted = [
        ...current
            .filter((item) => keptIds.has(item.itemId))
            .map((item) => ({ kind: "existing", itemId: item.itemId })),
        ...created.map((item) => ({
            exerciseId: item.exerciseId,
            kind: "created",
        })),
    ];
    const ordered = desired.length === predicted.length &&
        desired.every((ref, index) => sameOrderRef(ref, predicted[index]));
    return {
        creates,
        deletes,
        order: ordered ? null : desired,
        planId,
        sessionId,
    };
}
function sameOrderRef(a, b) {
    if (b === undefined) {
        return false;
    }
    if (a.kind === "existing") {
        return b.kind === "existing" && a.itemId === b.itemId;
    }
    return b.kind === "created" && a.exerciseId === b.exerciseId;
}
/**
 * `POST /me/exclusions` — *"never recommend this again"* (`D06`).
 *
 * There is no `readJson` here because there is no body: the route answers 201
 * the first time and 200 for an exercise already on the list, both empty. The
 * cap is a 409 carrying `exclusion.limit_reached` and `params: { max }` — the
 * caller must not swallow it, because it is the one failure the person can
 * act on.
 */
export async function addExclusion(fetcher, exerciseId) {
    const body = { exerciseId };
    const response = await fetcher(TRAINING_ROUTES.exclusions, {
        body: JSON.stringify(body),
        headers: JSON_HEADERS,
        method: "POST",
    });
    if (!response.ok) {
        throw await toApiError(response);
    }
}
//# sourceMappingURL=plan-write.js.map